<template>
  <div class="person-model-edit-form-general flex flex-col">
    <div class="space-y-2">
      <div class="flex flex-col gap-y-2">
        <label>
          <span class="sr-only block text-sm">Voornaam</span>
          <InputText
            v-model="person.firstName"
            placeholder="Voornaam"
            ref="inputFirstName"
            @blur="v$Person.firstName?.$touch()"
            :errors="v$Person.firstName?.$errors"
          />
        </label>

        <label>
          <span class="sr-only block text-sm">Achternaam</span>
          <InputText
            v-model="person.lastName"
            placeholder="Achternaam"
            ref="inputLastName"
            @blur="v$Person.lastName?.$touch()"
            :errors="v$Person.lastName?.$errors"
          />
        </label>

        <label v-if="hasServiceProviderRole">
          <span class="sr-only block text-sm">Burgerservicenummer</span>
          <InputText
            v-model="person.citizenNumber"
            placeholder="Burgerservicenummer"
            @blur="v$Person.citizenNumber?.$touch()"
            :errors="v$Person.citizenNumber?.$errors"
          />
        </label>
      </div>
      <div v-if="unknownKnownBusinessEntityContactForPerson" class="flex flex-col gap-y-2">
        <label>
          <span class="sr-only block text-sm">Telefoonnummer</span>
          <InputPhoneNumber
            v-model="unknownKnownBusinessEntityContactForPerson.phoneNumber"
            placeholder="Telefoonnummer"
            ref="inputPhoneNumber"
            class="pl-24"
            :errors="v$UnknownKBEC.phoneNumber?.$errors"
            @blur="v$UnknownKBEC.phoneNumber?.$touch()"
          />
        </label>

        <label>
          <span class="sr-only block text-sm">E-mailadres</span>
          <InputText
            v-model="unknownKnownBusinessEntityContactForPerson.email"
            placeholder="E-mailadres"
            type="email"
            :errors="v$UnknownKBEC.email.$errors"
            @blur="v$UnknownKBEC.email.$touch()"
          />
        </label>
      </div>

      <div v-if="!isCreating && unknownKnownBusinessEntityContactForPerson" class="mt-2 space-y-2">
        <MultiAlert>Deze persoon is nog niet gekoppeld aan een bedrijf</MultiAlert>
      </div>

      <KnownBusinessEntityContactComponent
        v-if="privateKnownBusinessEntityForPerson"
        v-model="privateKnownBusinessEntityForPerson"
        @kbec-changed="onPbuModified($event)"
        @address-cleared="person.address.clear()"
      >
        <template v-if="person.address" #extend-form="options">
          <li>
            <span class="sr-only block text-sm">Straat</span>
            <InputText
              v-model="person.address.street"
              placeholder="Straat"
              :disabled="options.disabled"
              class="private-person__street"
              @blur="v$Person.address.street.$touch()"
              :errors="v$Person.address.street ? v$Person.address.street.$errors : undefined"
            />
          </li>
          <li>
            <span class="sr-only block text-sm">Huisnummer</span>
            <InputText
              v-model="person.address.houseNumber"
              placeholder="Huisnummer"
              :disabled="options.disabled"
              class="private-person__house-number"
              @blur="v$Person.address.houseNumber.$touch()"
              :errors="
                v$Person.address.houseNumber ? v$Person.address.houseNumber.$errors : undefined
              "
            />
          </li>
          <li>
            <span class="sr-only block text-sm">Postcode</span>
            <InputText
              v-model="person.address.postalCode"
              placeholder="Postcode"
              :disabled="options.disabled"
              class="private-person__postal-code"
              @blur="v$Person.address.postalCode.$touch()"
              :errors="
                v$Person.address.postalCode ? v$Person.address.postalCode.$errors : undefined
              "
            />
          </li>
          <li>
            <span class="sr-only block text-sm">Land</span>
            <InputText
              v-model="person.address.country"
              placeholder="Land"
              :disabled="options.disabled"
              class="private-person__country"
              @blur="v$Person.address.country.$touch()"
              :errors="v$Person.address.country ? v$Person.address.country.$errors : undefined"
            />
          </li>
        </template>
      </KnownBusinessEntityContactComponent>

      <KnownBusinessEntityContactList
        v-model="nonPrivateKnownBusinessEntityContactsForPerson"
        @kbec-changed="onPbuModified($event)"
        ref="knownBusinessEntityContactList"
      />
      <div class="flex flex-row gap-x-2">
        <GeneralButton
          v-if="!isCreating && !isAddingBusiness"
          :class="{ 'opacity-50': isAddingAnything }"
          :disabled="isAddingAnything"
          :icon="['fas', 'plus']"
          size="small"
          @click.stop="isAddingBusiness = true"
          ref="addKnownBusinessEntityButton"
        >
          Bedrijf
        </GeneralButton>

        <GeneralButton
          v-if="!isCreating && !isPrivateIndividual && !isAddingBusiness"
          @click.stop="markPrivateIndividual"
          :class="{ 'opacity-50': isAddingAnything }"
          :disabled="isAddingAnything"
          variant="outlined"
          size="small"
          :icon="['fas', 'plus']"
          tabindex="-1"
        >
          Particulier
        </GeneralButton>
      </div>

      <div v-if="isAddingBusiness">
        <span class="sr-only block text-sm"> Bedrijf </span>
        <div
          class="border-surface gray-lighten-2 flex w-full flex-col rounded-md border-2 p-2"
          :class="isSaving ? 'opacity-50' : ''"
        >
          <label>
            <KnownBusinessEntityCombobox
              class="flex-1"
              :items="potentialKnownBusinessEntitiesForPerson"
              items-key-name="id"
              items-value-name="name"
              input-placeholder="Bedrijf"
              creation-option
              grow
              :creation-model-type="KnownBusinessEntity"
              auto-select-newly-created-model
              auto-focus
              @item-selected="onKnownBusinessEntitySelected"
            >
              <template #before-input>
                <FontAwesomeIcon size="sm" icon="building" title="Bedrijf" />
              </template>
            </KnownBusinessEntityCombobox>
          </label>
          <div class="mt-2">
            <GeneralButton @click="isAddingBusiness = false"> Annuleren </GeneralButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import KnownBusinessEntityContactList from "@/components/known_business_entity_contact/KnownBusinessEntityContactList.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { computed, inject, onUnmounted, type Ref, ref, watchEffect } from "vue"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store.ts"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store.ts"
import { storeToRefs } from "pinia"
import { Person } from "@/models/person.model"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { RoleTypes } from "@/enums/role-types"
import { KnownBusinessEntityContactController } from "@/controllers/model-controllers/known-business-entity-contact-controller"
import { onMounted } from "vue"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import InputText from "@/components/inputs/InputText.vue"
import InputPhoneNumber from "@/components/inputs/InputPhoneNumber.vue"
import {
  citizenNumberValidator,
  emailValidator,
  phoneNumberValidator,
  requiredValidator
} from "@/helper/validation/validators"
import type { PersonController } from "@/controllers/model-controllers/person-controller"
import { handleNamesForInitialText } from "@/composables/form/initial-text-names-helper"
import { ModelFactory } from "@/factories/model-factory"
import { usePersonStore } from "@/store/persons.store"
import useVuelidate, { type ValidationArgs } from "@vuelidate/core"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import KnownBusinessEntityContactComponent from "@/components/known_business_entity_contact/KnownBusinessEntityContactComponent.vue"
import MultiAlert from "@/components/alerts/MultiAlert.vue"
import {
  KBECEmailIsUniqueValidator,
  personPhoneNumberIsUniqueValidator
} from "@/helper/validation/known-business-entity-contact-validation"
import { formFocusHack } from "@/helper/form-helper"
import KnownBusinessEntityCombobox from "@/components/known_business_entity/KnownBusinessEntityCombobox.vue"

const knownBusinessEntitiesContactsStore = useKnownBusinessEntitiesContactsStore()
const knownBusinessEntityStore = useKnownBusinessEntityStore()
const { knownBusinessEntitiesContacts } = storeToRefs(knownBusinessEntitiesContactsStore)
const { knownBusinessEntities } = storeToRefs(knownBusinessEntityStore)

const selectedKnownBusinessEntity: Ref<KnownBusinessEntity | null | undefined> = ref(null)
const lastModifiedKnownBusinessEntityContact: Ref<KnownBusinessEntityContact | null | undefined> =
  ref(null)

// Keep track which kbecs are modified.
// lastModifiedKnownBusinessEntityContact will be provided to the save callback if only one KBEC was modified.
const kbecsModified = new Set<KnownBusinessEntityContact>()

function onPbuModified(kbec: KnownBusinessEntityContact) {
  kbecsModified.add(kbec)
  lastModifiedKnownBusinessEntityContact.value = kbec
}

const isAddingBusiness = ref(false)
const isAddingPrivateIndividual = ref(false)
const isSaving = ref(false)

const knownBusinessEntityContactsForPerson = computed(() => {
  return knownBusinessEntitiesContacts.value.filter((kbec) => kbec.person.id === person.value?.id)
})

// KBECs that have a known business entity.
const nonPrivateKnownBusinessEntityContactsForPerson = computed(() => {
  return knownBusinessEntityContactsForPerson.value.filter((kbec) => kbec.knownBusinessEntity)
})

// KBEC that is a private individual.
const privateKnownBusinessEntityForPerson = computed(() => {
  return knownBusinessEntityContactsForPerson.value.find(
    (kbec) => !kbec.knownBusinessEntity && kbec.isPrivateIndividual
  )
})

// KBEC that is not a business or private individual.
const unknownKnownBusinessEntityContactForPerson = computed(() => {
  return knownBusinessEntityContactsForPerson.value.find(
    (kbec) => !kbec.knownBusinessEntity && !kbec.isPrivateIndividual
  )
})

const potentialKnownBusinessEntitiesForPerson = computed(() => {
  return knownBusinessEntities.value.filter(
    (knownBusinessEntity) =>
      !knownBusinessEntityContactsForPerson.value
        .map((kbec) => kbec.knownBusinessEntity?.id)
        .includes(knownBusinessEntity.id)
  )
})

const hasServiceProviderRole = computed(() =>
  knownBusinessEntityContactsForPerson.value.some((kbec) =>
    kbec.hasRole(RoleTypes.SERVICE_PROVIDER)
  )
)

const isPrivateIndividual = computed(
  () =>
    !!knownBusinessEntityContactsForPerson.value.find(
      (kbec) => !kbec.knownBusinessEntity && kbec.isPrivateIndividual
    )
)

const isAddingAnything = computed(() => isAddingBusiness.value || isAddingPrivateIndividual.value)

const personController: PersonController = inject("personController")!
const isCreating: Ref<boolean> = inject("isCreating")!
const creationParameters: {
  focusRoleButton: KnownBusinessEntityContact
  focusNewKnownBusinessEntityButton: boolean
} = inject("creationParameters")!

const rules: ValidationArgs = {
  firstName: { requiredValidator },
  lastName: { requiredValidator },
  citizenNumber: { citizenNumberValidator },
  address: {
    street: {},
    houseNumber: {},
    postalCode: {},
    country: {}
  }
}
const {
  model: person,
  initialText,
  closeForm,
  saveHandle
} = useFormConfigurator<Person>({
  customSaveHandler,
  autoClose: false // Prevent closing the initial Person-only form when saving
})

const isFormInvalid: Ref<boolean> = inject("isFormInvalid")!
const v$Person = useVuelidate(rules, person.value!)

const rulesKBEC = {
  email: { emailValidator, KBECEmailIsUniqueValidator },
  phoneNumber: {
    phoneNumberValidator,
    KBECPhoneNumberIsUniqueValidator: personPhoneNumberIsUniqueValidator
  }
}
// Important to pass ref (without .value) because else the initial email and phone number will not be validated.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
// TODO: Fix this
const v$UnknownKBEC = useVuelidate(rulesKBEC, unknownKnownBusinessEntityContactForPerson)
watchEffect(() => {
  isFormInvalid.value = [v$Person.value.$invalid, v$UnknownKBEC.value.$invalid].some((v) => v)
})

const inputFirstName = ref()
const inputLastName = ref()

let initialKnownBusinessEntityContact: KnownBusinessEntityContact | undefined
onMounted(() => {
  if (isCreating.value) {
    // Every person should be saved with at least one KBEC. Create a new KBEC and associate it with the person.
    initialKnownBusinessEntityContact = ModelFactory.createModel(KnownBusinessEntityContact)
    initialKnownBusinessEntityContact.person = person.value!

    // Push the new KBEC to the store so it can be used in the list. Note that it is incomplete and unsaved at this point
    // so we must remove it from the store if the user cancels the creation.
    knownBusinessEntitiesContacts.value.push(initialKnownBusinessEntityContact)
    if (initialText) {
      handleNamesForInitialText(person, initialText, inputFirstName, inputLastName)
    } else {
      formFocusHack(inputFirstName, person, "firstName")
    }
  } else {
    formFocusHack(inputFirstName, person, "firstName")
  }
})

onUnmounted(() => {
  if (initialKnownBusinessEntityContact && !initialKnownBusinessEntityContact.id) {
    // Make sure to remove the new KBEC from the store if it was not saved, otherwise the list will continue contain the
    // incomplete KBEC
    knownBusinessEntitiesContacts.value.splice(
      knownBusinessEntitiesContacts.value.indexOf(initialKnownBusinessEntityContact),
      1
    )
  }
})

const knownBusinessEntityContactList = ref()
const addKnownBusinessEntityButton = ref()
onMounted(() => {
  if (creationParameters?.focusRoleButton) {
    setTimeout(() => {
      knownBusinessEntityContactList.value.focusRoleButton(creationParameters?.focusRoleButton)
    })
  } else if (creationParameters?.focusNewKnownBusinessEntityButton) {
    setTimeout(() => {
      addKnownBusinessEntityButton.value.focus()
    })
  }
})

const personStore = usePersonStore()
const dialogStore = useDialogStore()
const { persons } = storeToRefs(personStore)
async function customSaveHandler() {
  if (isCreating.value) {
    // Create the new KBEC (and nested person will be created as well).
    const remoteKBEC = await knownBusinessEntityContactController.create(
      unknownKnownBusinessEntityContactForPerson.value!,
      undefined,
      true
    )

    // Update the person model with the newly created Person.
    Object.assign(person.value, remoteKBEC!.person)

    // Add to persons collection
    persons.value.push(person.value)

    setTimeout(() => {
      addKnownBusinessEntityButton.value.focus()
    })
  } else if (unknownKnownBusinessEntityContactForPerson.value) {
    dialogStore.currentDialog = {
      title: "Geen bedrijf toegewezen",
      body: `${person.value.fullName} heeft geen bedrijf en is niet als particulier gemarkeerd. Wil je opslaan als onbekend?`,
      type: DialogTypes.CONFIRM,
      onConfirm: updateAndClose,
      textConfirm: "Ja, opslaan als onbekend",
      textCancel: "Nee, bedrijf toevoegen"
    }
    onPbuModified(unknownKnownBusinessEntityContactForPerson.value)
  } else {
    await updateAndClose()
  }
}

async function updateAndClose() {
  await personController.update(person.value)
  if (saveHandle) {
    if (kbecsModified.size === 1) {
      // Only provide the last modified KBEC if it is the only one that was modified.
      // This is to prevent confusion when the user edits multiple and there is no unambiguous way to determine which
      // one should be selected in the combobox.

      // The person might have changed, so we need to update the person on the KBEC.
      Object.assign(lastModifiedKnownBusinessEntityContact.value!.person, person.value)
      saveHandle(lastModifiedKnownBusinessEntityContact.value!)
    } else {
      saveHandle(person.value)
    }
  }
  closeForm()
}

const knownBusinessEntityContactController: KnownBusinessEntityContactController = inject(
  "knownBusinessEntityContactController"
)!
async function onKnownBusinessEntitySelected(knownBusinessEntity: KnownBusinessEntity) {
  isSaving.value = true

  // Check if the person already has a KBEC with the selected known business entity. This could happen if a KVK-entry
  // was selected in the KnownBusinessEntityModelEditForm, but the resulting KBE already existed and was linked to this
  // person.
  const existingKbec = knownBusinessEntityContactsForPerson.value.find(
    (kbec) => kbec.knownBusinessEntity?.id === knownBusinessEntity.id
  )
  if (existingKbec) {
    selectedKnownBusinessEntity.value = null
    isAddingBusiness.value = false
    isSaving.value = false
    return
  }

  let newKnownBusinessEntityContact
  if (unknownKnownBusinessEntityContactForPerson.value) {
    newKnownBusinessEntityContact = unknownKnownBusinessEntityContactForPerson.value
    newKnownBusinessEntityContact.knownBusinessEntity = knownBusinessEntity
    await knownBusinessEntityContactController.update(newKnownBusinessEntityContact)
  } else {
    const knownBusinessEntityContact = ModelFactory.createModel(KnownBusinessEntityContact)
    knownBusinessEntityContact.person = person.value!
    knownBusinessEntityContact.knownBusinessEntity = knownBusinessEntity
    newKnownBusinessEntityContact = await knownBusinessEntityContactController.create(
      knownBusinessEntityContact,
      undefined,
      true
    )
  }

  onPbuModified(newKnownBusinessEntityContact!)
  selectedKnownBusinessEntity.value = null
  isAddingBusiness.value = false
  isSaving.value = false

  knownBusinessEntityContactList.value.focusEmailInput(newKnownBusinessEntityContact)
}

async function markPrivateIndividual() {
  isAddingPrivateIndividual.value = true
  if (unknownKnownBusinessEntityContactForPerson.value) {
    // Copy to avoid reactivity issues. These happen because setting isPrivateIndividual to true will re-compute
    // unknownKnownBusinessEntityContact, and set it to undefined.
    const copy = unknownKnownBusinessEntityContactForPerson.value
    copy.isPrivateIndividual = true
    await knownBusinessEntityContactController.update(copy)
    onPbuModified(copy)
  } else {
    const knownBusinessEntityContact = ModelFactory.createModel(KnownBusinessEntityContact)
    knownBusinessEntityContact.person = person.value!
    knownBusinessEntityContact.isPrivateIndividual = true
    await knownBusinessEntityContactController.create(knownBusinessEntityContact, undefined, true)
    onPbuModified(knownBusinessEntityContact)
  }
  isAddingPrivateIndividual.value = false
}
</script>

<style scoped></style>
